/**
 * A document's picture as the editor draws it, Word's: selected by a click (a picture behind
 * the words too, where no word is), resized by its corner handles, dragged anywhere when it is
 * behind or in front of the words (in line or square, ProseMirror moves it among the blocks),
 * and the layout options button beside it choosing how it sits (`state/documents/pictures.ts`).
 * A free picture stays on its sheet of paper (Word keeps it on its page): dragged no further
 * than the sheet's edges, and above its bottom margin (where the print layout ends a sheet).
 * Each gesture ends in one change of the picture's attributes: one undo.
 */
import type { Node as PMNode } from "prosemirror-model";
import { NodeSelection } from "prosemirror-state";
import type { EditorView, NodeView } from "prosemirror-view";
import { WIDTH_RANGE, WRAPS, isFree, pictureCss, pictureLook, type PictureLook, type Sheet, type Wrap } from "../../../state/documents";
import { setPicture } from "./commands";
import { PICTURE_DRAG_PX } from "./constants";

/** The corners a picture is resized from. */
const CORNERS = ["nw", "ne", "sw", "se"] as const;

/** What a picture needs from the editor. */
export interface PictureDeps {
  /** A picture's address (`null` until read). */
  url(src: string): string | null;
  /** Whether this person may change the document. */
  canEdit(): boolean;
  /** The pictures shown, refreshed when a file is read. */
  shown: Set<() => void>;
  /** The page's sheet of paper, in pixels. */
  sheet(): Sheet;
}

/** Writes `css` on `el`, clearing what an earlier placement set and this one does not. */
function place(el: HTMLElement, css: Record<string, string>, keys: readonly string[]): void {
  for (const k of keys) el.style.removeProperty(k);
  for (const [k, v] of Object.entries(css)) el.style.setProperty(k, v);
}

/** Every property a placement may set, so a new one clears the old. */
const FIGURE_KEYS = ["float", "width", "margin", "text-align", "position", "height", "z-index"];
/** The same for the frame around the image. */
const FRAME_KEYS = ["position", "left", "top", "width"];

/** The node view of picture `node`. */
export function pictureView(node: PMNode, view: EditorView, getPos: () => number | undefined, deps: PictureDeps): NodeView {
  const figure = document.createElement("figure");
  figure.className = "doc-image";
  const frame = document.createElement("span");
  frame.className = "doc-image-frame";
  const img = document.createElement("img");
  img.draggable = false;
  frame.append(img);
  for (const corner of CORNERS) {
    const handle = document.createElement("span");
    handle.className = "doc-image-handle";
    handle.dataset.imageHandle = corner;
    frame.append(handle);
  }
  const layoutButton = document.createElement("button");
  layoutButton.type = "button";
  layoutButton.className = "doc-image-layout";
  layoutButton.dataset.imageLayout = "";
  layoutButton.title = "Layout options: how the picture sits among the words";
  layoutButton.setAttribute("aria-label", "Layout options");
  layoutButton.textContent = "⧉";
  const options = document.createElement("div");
  options.className = "doc-image-options";
  options.hidden = true;
  options.setAttribute("role", "menu");
  for (const w of WRAPS) {
    const b = document.createElement("button");
    b.type = "button";
    b.dataset.imageWrap = w.id;
    b.setAttribute("role", "menuitemradio");
    b.textContent = w.label;
    options.append(b);
  }
  frame.append(layoutButton, options);
  const caption = document.createElement("figcaption");
  figure.append(frame, caption);

  let current = node;
  const look = (): PictureLook => pictureLook(current.attrs);
  const refresh = () => {
    const src = String(current.attrs.src);
    const l = look();
    figure.dataset.src = src;
    figure.dataset.wrap = l.wrap;
    figure.dataset.align = l.align;
    if (current.attrs.id) figure.dataset.block = String(current.attrs.id);
    const address = deps.url(src);
    if (address) img.src = address;
    else img.removeAttribute("src");
    img.alt = String(current.attrs.alt || src);
    const css = pictureCss(l);
    place(figure, css.figure, FIGURE_KEYS);
    place(frame, css.frame, FRAME_KEYS);
    caption.textContent = String(current.attrs.alt ?? "");
    caption.hidden = !current.attrs.alt || isFree(l.wrap);
    for (const b of options.querySelectorAll<HTMLElement>("[data-image-wrap]")) b.setAttribute("aria-checked", String(b.dataset.imageWrap === l.wrap));
    // A free picture is moved by its own drag: never the browser's, which would move its block in the words.
    figure.draggable = !isFree(l.wrap);
  };
  refresh();
  // ProseMirror makes the picture draggable once it is drawn: said again after.
  queueMicrotask(() => (figure.draggable = !isFree(look().wrap)));
  deps.shown.add(refresh);

  /** Selects this picture. */
  const select = () => {
    const pos = getPos();
    if (pos === undefined) return;
    view.dispatch(view.state.tr.setSelection(NodeSelection.create(view.state.doc, pos)));
    // The editor keeps the keyboard (Ctrl+Z, Delete), even when the press was the picture's own.
    view.focus();
  };
  /** Changes this picture's attributes (one undo). */
  const change = (patch: Partial<PictureLook>) => {
    const pos = getPos();
    if (pos !== undefined) setPicture(pos, patch)(view.state, view.dispatch);
  };

  /** Follows a press on the picture or one of its handles until it is let go. */
  const press = (e: PointerEvent) => {
    if (e.button !== 0) return;
    const target = e.target as HTMLElement;
    if (target.closest(".doc-image-options, [data-image-layout]")) return;
    const corner = target.dataset.imageHandle as (typeof CORNERS)[number] | undefined;
    select();
    if (!deps.canEdit()) return;
    const l = look();
    // In line or square, moving the picture is ProseMirror's own drag among the blocks.
    if (!corner && !isFree(l.wrap)) return;
    e.preventDefault();
    const [x0, y0] = [e.clientX, e.clientY];
    const column = view.dom.clientWidth || 1;
    const startWidth = frame.getBoundingClientRect().width;
    const startHeight = img.getBoundingClientRect().height;
    let moved = false;
    let next: Partial<PictureLook> = {};
    const startRect = frame.getBoundingClientRect();
    const bounds = sheetAround(view.dom, figure, deps.sheet().margins[2]);
    const move = (ev: PointerEvent) => {
      const [dx, dy] = [ev.clientX - x0, ev.clientY - y0];
      if (!moved && Math.hypot(dx, dy) < PICTURE_DRAG_PX) return;
      moved = true;
      if (corner) {
        const grow = corner.endsWith("e") ? dx : -dx;
        const px = Math.max(16, startWidth + grow);
        const width = Math.round(Math.min(WIDTH_RANGE[1], Math.max(WIDTH_RANGE[0], (px / column) * 100)));
        next = { width };
        // A free picture keeps its other side where it was.
        if (isFree(l.wrap) && corner.endsWith("w")) next.x = Math.round(l.x + startWidth - (width / 100) * column);
        if (isFree(l.wrap) && corner.startsWith("n")) next.y = Math.round(l.y + startHeight - (startHeight * ((width / 100) * column)) / startWidth);
      } else {
        // Kept on its sheet, whole when it fits.
        const keep = (d: number, low: number, high: number, start: number, size: number) => (size > high - low ? low - start : Math.min(high - size - start, Math.max(low - start, d)));
        const kx = bounds ? keep(dx, bounds.left, bounds.right, startRect.left, startRect.width) : dx;
        const ky = bounds ? keep(dy, bounds.top, bounds.bottom, startRect.top, startRect.height) : dy;
        next = { x: Math.round(l.x + kx), y: Math.round(l.y + ky) };
      }
      // Shown at once; written when let go.
      const css = pictureCss({ ...l, ...next });
      place(frame, css.frame, FRAME_KEYS);
      if (l.wrap === "square") place(figure, css.figure, FIGURE_KEYS);
    };
    const up = () => {
      removeEventListener("pointermove", move);
      removeEventListener("pointerup", up);
      if (moved) change(next);
      else refresh();
    };
    addEventListener("pointermove", move);
    addEventListener("pointerup", up);
  };
  frame.addEventListener("pointerdown", press);
  // A free picture is moved by its own drag, never the browser's.
  const ownDrag = (e: Event) => isFree(look().wrap) && e.preventDefault();
  frame.addEventListener("mousedown", ownDrag);
  figure.addEventListener("dragstart", ownDrag);

  layoutButton.addEventListener("mousedown", (e) => e.preventDefault());
  layoutButton.addEventListener("click", () => {
    options.hidden = !options.hidden;
  });
  options.addEventListener("mousedown", (e) => e.preventDefault());
  options.addEventListener("click", (e) => {
    const wrap = (e.target as HTMLElement).closest<HTMLElement>("[data-image-wrap]")?.dataset.imageWrap as Wrap | undefined;
    if (!wrap) return;
    options.hidden = true;
    if (deps.canEdit()) change({ wrap });
  });

  return {
    dom: figure,
    update(next) {
      if (next.type !== current.type) return false;
      current = next;
      refresh();
      return true;
    },
    selectNode: () => figure.classList.add("selected"),
    deselectNode: () => {
      figure.classList.remove("selected");
      options.hidden = true;
    },
    // The picture's own gestures and buttons are not ProseMirror's (it still drags one in the words).
    stopEvent: (e) => {
      const t = e.target as HTMLElement | null;
      if (t?.closest?.(".doc-image-options, [data-image-layout], [data-image-handle]")) return true;
      return isFree(look().wrap) && (e.type.startsWith("pointer") || e.type.startsWith("mouse") || e.type === "dragstart");
    },
    ignoreMutation: () => true,
    destroy: () => deps.shown.delete(refresh),
  };
}

/** Where on the sheet of paper `figure` is anchored on a picture may go, in page coordinates: between the gaps the print layout drew (`plugins.ts`), within the paper, above its bottom margin `margin`. */
function sheetAround(text: HTMLElement, figure: HTMLElement, margin: number): { left: number; right: number; top: number; bottom: number } | null {
  const paper = text.closest(".doc-paper")?.getBoundingClientRect();
  if (!paper) return null;
  const anchor = figure.getBoundingClientRect().top;
  let top = paper.top;
  let bottom = paper.bottom;
  for (const gap of text.querySelectorAll(".doc-gap")) {
    const desk = gap.querySelector(".doc-gap-desk")?.getBoundingClientRect();
    if (!desk) continue;
    if (desk.bottom <= anchor) top = Math.max(top, desk.bottom);
    else bottom = Math.min(bottom, desk.top);
  }
  // A pixel kept: the picture's place is rounded, and its end must stay on the sheet.
  return { left: paper.left, right: paper.right, top, bottom: bottom - margin - 1 };
}

/** Whether page point `x`, `y` is on words of `block` (a line of text drawn there). */
function onWords(block: Element, x: number, y: number): boolean {
  const range = document.createRange();
  range.selectNodeContents(block);
  for (const r of range.getClientRects()) if (x >= r.left && x <= r.right && y >= r.top && y <= r.bottom) return true;
  return false;
}

/** The picture behind the words under page point `x`, `y` where no word is (Word selects it there), if any. */
export function pictureBehind(root: HTMLElement, x: number, y: number): HTMLElement | null {
  const under = document.elementFromPoint(x, y);
  if (under?.closest(".doc-image")) return null;
  for (const figure of root.querySelectorAll<HTMLElement>('figure.doc-image[data-wrap="behind"]')) {
    const frame = figure.querySelector(".doc-image-frame");
    const r = frame?.getBoundingClientRect();
    if (!r || x < r.left || x > r.right || y < r.top || y > r.bottom) continue;
    const block = under?.closest(".doc-text > *");
    if (!block || !onWords(block, x, y)) return figure;
  }
  return null;
}
